import { FC } from 'react';

import { DiagramMessageCard } from '@/modules/artifactStudio/components/DiagramMessageCard';
import { ConfirmationMessageRenderer } from '@/modules/household/chat/components/messages/interactive/confirmationMessage/ConfirmationMessageRenderer';
import { FreeFormQuestionMessageRenderer } from '@/modules/household/chat/components/messages/interactive/freeFormQuestionMessage/FreeFormQuestionMessageRenderer';
import { GroupedQuestionMessageRenderer } from '@/modules/household/chat/components/messages/interactive/groupedQuestionMessage/GroupedQuestionMessageRenderer';
import {
  MultipleChoiceMultiSelectMessageRenderer,
  MultipleChoiceSingleSelectMessageRenderer,
} from '@/modules/household/chat/components/messages/interactive/multipleChoiceQuestionMessage/MultipleChoiceQuestionMessageRenderer';
import { MessageType } from '@/types/schema';

import { HouseholdChatMessage } from '../../contexts/householdChat.context';
import { TextMessageRenderer } from './TextMessageRenderer';

export interface MessageRendererProps {
  message: HouseholdChatMessage;
}

/**
 * Renderer registry keyed by message type — the extensibility spine for rich
 * assistant content. Future types (TABLE, DOCUMENT_PREVIEW, ARTIFACT, …) add
 * a typed column on Message, then register their renderer here. Using a total
 * Record means adding a MessageType enum value fails compilation until a
 * renderer is registered.
 */
export const MESSAGE_RENDERERS: Record<
  MessageType,
  FC<MessageRendererProps>
> = {
  [MessageType.Text]: TextMessageRenderer,
  [MessageType.DiagramMessage]: DiagramMessageCard,

  // Never shown: the transcript filters these rows out because each
  // interactive card already displays the user's response in place.
  [MessageType.GeneratedUserResponse]: () => null,

  // INTERACTIVE MESSAGES — types that solicit a response through in-card
  // actions. Each must also be listed in INTERACTIVE_MESSAGE_TYPES in
  // interactive/interactiveMessage.utils.ts so the conversation blocks on it.
  [MessageType.ConfirmationMessage]: ConfirmationMessageRenderer,
  [MessageType.FreeFormQuestionMessage]: FreeFormQuestionMessageRenderer,
  [MessageType.GroupedQuestionMessage]: GroupedQuestionMessageRenderer,
  [MessageType.MultipleChoiceSingleSelectMessage]:
    MultipleChoiceSingleSelectMessageRenderer,
  [MessageType.MultipleChoiceMultiSelectMessage]:
    MultipleChoiceMultiSelectMessageRenderer,
};
